import React, { useEffect, useState, useCallback } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const STATUS_COLORS = {
  DRAFT:     { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb' },
  ISSUED:    { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  PAID:      { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  OVERDUE:   { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  CANCELLED: { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb' }
};

const fmtINR = (n) => {
  if (n == null) return '₹0';
  return '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.DRAFT;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 12,
      fontSize: 11, fontWeight: 600, background: s.bg, color: s.color,
      border: `1px solid ${s.border}`, textTransform: 'uppercase', letterSpacing: 0.3
    }}>
      {status}
    </span>
  );
}

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (document.getElementById('razorpay-checkout-js')) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.id = 'razorpay-checkout-js';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

function MySubscriptionInvoices({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [payLoading, setPayLoading] = useState(null); // invoice_id being paid

  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': chainId,
    'x-property-id': propertyId,
    ...extra
  });

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/v1/subscription/my/invoices', { headers: headers() });
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) throw new Error('Server unavailable');
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || 'Failed to load invoices');
      setInvoices(json.invoices || json || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, chainId, propertyId]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const loadDetail = async (invoiceId) => {
    if (expandedId === invoiceId) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(invoiceId);
    setDetailLoading(true);
    try {
      const res = await fetch(`/api/v1/subscription/my/invoices/${invoiceId}`, { headers: headers() });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || 'Failed to load detail');
      setDetail(json);
    } catch (e) {
      setError(e.message);
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handlePay = async (invoice) => {
    const invoiceId = invoice.invoice_id || invoice.id;
    setPayLoading(invoiceId);
    setError('');
    setSuccess('');
    try {
      // 1. Initiate payment
      const initRes = await fetch('/api/v1/subscription/my/payments/initiate', {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ invoice_id: invoiceId })
      });
      const initJson = await initRes.json();
      if (!initRes.ok) throw new Error(initJson?.error || 'Failed to initiate payment');

      // 2. Load Razorpay script
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error('Failed to load Razorpay checkout');

      // 3. Open Razorpay checkout
      const options = {
        key: initJson.razorpay_key || initJson.key,
        amount: initJson.amount, // in paise
        currency: initJson.currency || 'INR',
        name: initJson.business_name || 'EASEPMS',
        description: `Invoice ${invoice.invoice_number || invoiceId}`,
        order_id: initJson.razorpay_order_id || initJson.order_id,
        handler: async function (response) {
          // 4. Verify payment
          try {
            const verifyRes = await fetch('/api/v1/subscription/my/payments/verify', {
              method: 'POST',
              headers: headers({ 'Content-Type': 'application/json' }),
              body: JSON.stringify({
                invoice_id: invoiceId,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              })
            });
            const verifyJson = await verifyRes.json();
            if (!verifyRes.ok) throw new Error(verifyJson?.error || 'Payment verification failed');
            setSuccess('Payment successful!');
            loadInvoices();
          } catch (e) {
            setError(e.message);
          } finally {
            setPayLoading(null);
          }
        },
        modal: {
          ondismiss: function () {
            setPayLoading(null);
          }
        },
        prefill: {
          email: initJson.email || '',
          contact: initJson.phone || ''
        },
        theme: {
          color: '#2563eb'
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (resp) {
        setError(`Payment failed: ${resp.error?.description || 'Unknown error'}`);
        setPayLoading(null);
      });
      rzp.open();
    } catch (e) {
      setError(e.message);
      setPayLoading(null);
    }
  };

  const isUnpaid = (status) => ['ISSUED', 'OVERDUE'].includes(status);

  const thStyle = {
    padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: 0.5, color: '#6b7280',
    borderBottom: '2px solid var(--theme-border, #e5e7eb)', whiteSpace: 'nowrap',
    background: 'var(--theme-surface, #f9fafb)'
  };
  const tdStyle = {
    padding: '10px 12px', fontSize: 13, borderBottom: '1px solid var(--theme-border, #e5e7eb)',
    color: 'var(--theme-text, #374151)', whiteSpace: 'nowrap'
  };

  return (
    <div style={{ padding: isMobile ? 12 : 24, maxWidth: 1200, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 20px', color: 'var(--theme-text, #1f2937)', fontSize: isMobile ? 18 : 22 }}>
        Invoices & Payments
      </h2>

      {error && (
        <div style={{
          padding: '10px 14px', marginBottom: 16, background: '#fef2f2',
          border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 13
        }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{
          padding: '10px 14px', marginBottom: 16, background: '#f0fdf4',
          border: '1px solid #bbf7d0', borderRadius: 6, color: '#16a34a', fontSize: 13
        }}>
          {success}
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading...</div>}

      {!loading && (
        <>
          {/* Mobile: card view */}
          {isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {invoices.map((inv) => {
                const invId = inv.invoice_id || inv.id;
                const expanded = expandedId === invId;
                return (
                  <div
                    key={invId}
                    style={{
                      background: 'var(--theme-surface, #fff)',
                      border: '1px solid var(--theme-border, #e5e7eb)',
                      borderRadius: 8, overflow: 'hidden'
                    }}
                  >
                    <div
                      onClick={() => loadDetail(invId)}
                      style={{ padding: 14, cursor: 'pointer' }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <strong style={{ fontSize: 14, color: 'var(--theme-text, #1f2937)' }}>
                          {inv.invoice_number || `INV-${invId}`}
                        </strong>
                        <StatusBadge status={inv.status} />
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                        {fmtDate(inv.period_start)} - {fmtDate(inv.period_end)}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--theme-text, #1f2937)' }}>
                          {fmtINR(inv.total_amount)}
                        </div>
                        {isUnpaid(inv.status) && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handlePay(inv); }}
                            disabled={payLoading === invId}
                            style={{
                              padding: '6px 16px', borderRadius: 6, border: 'none',
                              background: 'var(--theme-primary, #2563eb)', color: '#fff',
                              fontSize: 12, fontWeight: 600, cursor: 'pointer',
                              opacity: payLoading === invId ? 0.6 : 1
                            }}
                          >
                            {payLoading === invId ? 'Processing...' : 'Pay Now'}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Expanded detail */}
                    {expanded && (
                      <div style={{
                        padding: '0 14px 14px', borderTop: '1px solid var(--theme-border, #e5e7eb)'
                      }}>
                        {detailLoading ? (
                          <div style={{ padding: 16, color: '#6b7280', fontSize: 12 }}>Loading details...</div>
                        ) : detail && (
                          <div style={{ paddingTop: 12 }}>
                            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                              Issue Date: {fmtDate(detail.issue_date)} | Due Date: {fmtDate(detail.due_date)}
                            </div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                              <thead>
                                <tr>
                                  <th style={{ ...thStyle, padding: '6px 8px', fontSize: 10 }}>Module</th>
                                  <th style={{ ...thStyle, padding: '6px 8px', fontSize: 10 }}>Qty</th>
                                  <th style={{ ...thStyle, padding: '6px 8px', fontSize: 10 }}>Unit Price</th>
                                  <th style={{ ...thStyle, padding: '6px 8px', fontSize: 10 }}>Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(detail.lines || detail.items || []).map((ln, i) => (
                                  <tr key={i}>
                                    <td style={{ ...tdStyle, padding: '6px 8px', fontSize: 12 }}>
                                      {ln.module_name || ln.description || '-'}
                                    </td>
                                    <td style={{ ...tdStyle, padding: '6px 8px', fontSize: 12 }}>
                                      {ln.quantity ?? 1}
                                    </td>
                                    <td style={{ ...tdStyle, padding: '6px 8px', fontSize: 12 }}>
                                      {fmtINR(ln.unit_price)}
                                    </td>
                                    <td style={{ ...tdStyle, padding: '6px 8px', fontSize: 12 }}>
                                      {fmtINR(ln.line_total || ln.total)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {invoices.length === 0 && (
                <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>No invoices found.</div>
              )}
            </div>
          ) : (
            /* Desktop: table view */
            <div style={{ border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 8, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Invoice #</th>
                    <th style={thStyle}>Period</th>
                    <th style={thStyle}>Issue Date</th>
                    <th style={thStyle}>Due Date</th>
                    <th style={thStyle}>Amount</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => {
                    const invId = inv.invoice_id || inv.id;
                    const expanded = expandedId === invId;
                    return (
                      <React.Fragment key={invId}>
                        <tr
                          onClick={() => loadDetail(invId)}
                          style={{ cursor: 'pointer' }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--theme-hover, #f3f4f6)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = ''}
                        >
                          <td style={tdStyle}>{inv.invoice_number || `INV-${invId}`}</td>
                          <td style={tdStyle}>{fmtDate(inv.period_start)} - {fmtDate(inv.period_end)}</td>
                          <td style={tdStyle}>{fmtDate(inv.issue_date)}</td>
                          <td style={tdStyle}>{fmtDate(inv.due_date)}</td>
                          <td style={tdStyle}>{fmtINR(inv.total_amount)}</td>
                          <td style={tdStyle}><StatusBadge status={inv.status} /></td>
                          <td style={tdStyle}>
                            {isUnpaid(inv.status) && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handlePay(inv); }}
                                disabled={payLoading === invId}
                                style={{
                                  padding: '5px 14px', borderRadius: 5, border: 'none',
                                  background: 'var(--theme-primary, #2563eb)', color: '#fff',
                                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                  opacity: payLoading === invId ? 0.6 : 1
                                }}
                              >
                                {payLoading === invId ? 'Processing...' : 'Pay Now'}
                              </button>
                            )}
                          </td>
                        </tr>

                        {/* Expanded line items */}
                        {expanded && (
                          <tr>
                            <td colSpan={7} style={{
                              padding: 0, background: 'var(--theme-surface, #f9fafb)',
                              borderBottom: '1px solid var(--theme-border, #e5e7eb)'
                            }}>
                              {detailLoading ? (
                                <div style={{ padding: 20, color: '#6b7280', fontSize: 13 }}>Loading details...</div>
                              ) : detail && (
                                <div style={{ padding: '16px 24px' }}>
                                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: 'var(--theme-text, #374151)' }}>
                                    Line Items
                                  </div>
                                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                      <tr>
                                        <th style={{ ...thStyle, background: 'transparent' }}>Module</th>
                                        <th style={{ ...thStyle, background: 'transparent' }}>Qty</th>
                                        <th style={{ ...thStyle, background: 'transparent' }}>Unit Price</th>
                                        <th style={{ ...thStyle, background: 'transparent' }}>Line Total</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(detail.lines || detail.items || []).map((ln, i) => (
                                        <tr key={i}>
                                          <td style={tdStyle}>{ln.module_name || ln.description || '-'}</td>
                                          <td style={tdStyle}>{ln.quantity ?? 1}</td>
                                          <td style={tdStyle}>{fmtINR(ln.unit_price)}</td>
                                          <td style={tdStyle}>{fmtINR(ln.line_total || ln.total)}</td>
                                        </tr>
                                      ))}
                                      {(!detail.lines && !detail.items) && (
                                        <tr>
                                          <td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: '#6b7280' }}>
                                            No line items.
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                  <div style={{
                                    display: 'flex', justifyContent: 'flex-end', gap: 20,
                                    marginTop: 12, fontSize: 13, fontWeight: 600
                                  }}>
                                    {detail.subtotal != null && (
                                      <span>Subtotal: {fmtINR(detail.subtotal)}</span>
                                    )}
                                    {detail.tax_amount != null && (
                                      <span>Tax: {fmtINR(detail.tax_amount)}</span>
                                    )}
                                    <span style={{ color: 'var(--theme-primary, #2563eb)' }}>
                                      Total: {fmtINR(detail.total_amount)}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {invoices.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ ...tdStyle, textAlign: 'center', padding: 40, color: '#6b7280' }}>
                        No invoices found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default MySubscriptionInvoices;
